import { ErpAside, ErpDiv, ErpSpan, ErpNav, ErpUl, ErpLi, ErpButtonRaw, ErpA } from "@/components/erp";
import React, { useEffect, useMemo, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { activeMenuId } from '@/utils/sidebarNavigation'
import { useSidebarScroll } from '@/hooks/useSidebarScroll'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { togglePinned, expand, collapse, closeMobile } from '@/redux/slices/sidebarSlice'
import { toggleFavorite, toggleFavoritesWithPersistence } from '@/redux/slices/favoritesSlice'
import { menuGroups, hasPermission, type MenuItem } from '@/config/menu'
import { Pin, X, Puzzle } from 'lucide-react';
import { iconFor } from '@/lib/iconRegistry';

interface AddonModule {
  id: string | number
  name: string
  icon: string
  url: string
  permission: string
}

export const Sidebar: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const identity = useAppSelector(state => `${state.tenant?.tenantId ?? state.auth?.tenantId ?? 'local'}:${state.auth?.currentUser?.id ?? state.auth?.user?.id ?? 'anonymous'}`)

  const panelRef = useRef<HTMLElement>(null)
  const mode = useAppSelector((state) => state.sidebar.mode)
  const pinned = useAppSelector((state) => state.sidebar.pinned)
  const mobileOpen = useAppSelector((state) => state.sidebar.mobileOpen)
  const permissions = useAppSelector((state) => state.auth.Permissions)
  const allModules = useAppSelector((state) => state.auth.allmodules) as AddonModule[] | null
  const favoriteIds = useAppSelector((state) => state.favorites.favoriteIds)

  const visibleItems = useMemo(() => menuGroups.flatMap(group => group.items).filter(item => hasPermission(permissions, item.permissions)), [permissions])
  const activeId = activeMenuId(visibleItems, location.pathname, location.search)
  const scrollRef = useSidebarScroll(`erp:sidebar-scroll:${identity}`, location.pathname + location.search, activeId, mobileOpen || !window.matchMedia('(max-width: 1023px)').matches)
  const isExpanded = mode === 'expanded'
  const isSidebarOpen = mobileOpen || isExpanded || pinned

  const favoriteItems = useMemo<MenuItem[]>(() => {
    return menuGroups
      .flatMap((g) => g.items)
      .filter((item) => favoriteIds.includes(item.id))
      .filter((item) => hasPermission(permissions, item.permissions))
  }, [favoriteIds, permissions])

  const addonModules = useMemo<AddonModule[]>(() => allModules ?? [], [allModules])

  // Collapse the rail automatically on narrow screens. Track the latest state
  // in a ref so a continuous drag-resize only dispatches on the transition.
  const sidebarStateRef = useRef({ mode, mobileOpen })
  useEffect(() => {
    sidebarStateRef.current = { mode, mobileOpen }
  })

  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 1023px)')
    const onResize = () => {
      if (mobileQuery.matches) {
        if (sidebarStateRef.current.mode !== 'collapsed') dispatch(collapse())
      }
      if (sidebarStateRef.current.mobileOpen) dispatch(closeMobile())
    }
    onResize()
    mobileQuery.addEventListener('change', onResize)
    return () => {
      mobileQuery.removeEventListener('change', onResize)
    }
  }, [dispatch])

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>('button, a[href]')?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); dispatch(closeMobile()); }
      if (event.key !== 'Tab') return;
      const items = [...(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex="0"]') ?? [])];
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { cancelAnimationFrame(frame); document.removeEventListener('keydown', onKeyDown); if (previous?.isConnected) previous.focus(); };
  }, [mobileOpen, dispatch]);

  const onHoverEnter = () => {
    if (!pinned && typeof window !== 'undefined' && !window.matchMedia('(max-width: 1023px)').matches) {
      dispatch(expand())
    }
  }
  const onHoverLeave = () => {
    if (!pinned && typeof window !== 'undefined' && !window.matchMedia('(max-width: 1023px)').matches) {
      dispatch(collapse())
    }
  }

  // Persist BEFORE dispatching: the reducer is pure, so localStorage writes
  // happen here (the store update stays deterministic + testable).
  const handleToggleFavorite = (id: string) => {
    toggleFavoritesWithPersistence(id, favoriteIds)
    dispatch(toggleFavorite(id))
  }

  const isFavorite = (id: string) => favoriteIds.includes(id)

  const widthClass = isSidebarOpen ? 'w-64' : 'w-[4.5rem]'
  const mobileClass = mobileOpen ? 'flex' : 'hidden lg:flex'
  const labelClass = 'min-w-0 flex-1 whitespace-normal break-words [overflow-wrap:anywhere] leading-snug'
  const baseLink = `flex min-h-11 min-w-0 items-center gap-2.5 rounded-lg py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isSidebarOpen ? 'px-3' : 'justify-center px-0'}`
  const closeAfterNavigation = () => { if (mobileOpen) dispatch(closeMobile()) }
  const itemIcon = (key: string) => iconFor(key, 20) || <Puzzle size={20} />
  const renderItem = (item: MenuItem, prefix = '') => {
    const label = t(item.nameKey), favorite = isFavorite(item.id), isActive = item.id === activeId
    const content = <>
      <ErpSpan aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center">{itemIcon(item.icon)}</ErpSpan>
      {isSidebarOpen && <ErpSpan className={labelClass}>{label}</ErpSpan>}
      {isSidebarOpen && item.external && <ErpSpan className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{t('Addon')}</ErpSpan>}
    </>
    return <ErpLi key={`${prefix}${item.id}`} className="group relative min-w-0">
      {item.external ? <ErpA href={item.path} target="_blank" rel="noopener noreferrer" title={label} aria-label={label}
        className={`${baseLink} text-muted-foreground hover:bg-accent hover:text-accent-foreground`} onClick={closeAfterNavigation}>{content}</ErpA>
        : <Link data-menu-id={item.id} aria-current={isActive ? 'page' : undefined} to={item.path || ''} title={label} aria-label={label} onClick={closeAfterNavigation}
          className={`${baseLink} ${isSidebarOpen ? 'pe-10' : ''} ${isActive ? 'bg-primary text-primary-foreground shadow-sm font-semibold ring-2 ring-inset ring-primary-foreground/30' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}>{content}{isActive && <ErpSpan aria-hidden="true" className="absolute start-0 top-3 bottom-3 w-1 rounded-full bg-current" />}</Link>}
      {isSidebarOpen && !item.external && <ErpButtonRaw type="button" aria-pressed={favorite} aria-label={`${t(favorite ? 'Unpin' : 'Pin')} ${label}`}
        className={`absolute end-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md bg-card text-foreground shadow-sm ring-1 ring-border transition-opacity hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${favorite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 max-lg:opacity-100'}`}
        onClick={() => handleToggleFavorite(item.id)}><Pin size={14} className={favorite ? '' : 'rotate-45'} /></ErpButtonRaw>}
    </ErpLi>
  }
  return <>
    {mobileOpen && <ErpDiv data-testid="sidebar-backdrop" aria-hidden="true" className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => dispatch(closeMobile())} />}
    <ErpAside ref={panelRef} aria-label={t('ui.navigation')} role={mobileOpen ? 'dialog' : undefined} aria-modal={mobileOpen || undefined}
      className={`fixed inset-y-0 start-0 z-50 shrink-0 flex-col border-e border-border bg-card transition-[width] duration-200 ease-in-out motion-reduce:transition-none lg:relative ${widthClass} ${mobileClass}`}
      onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
      <ErpDiv className="relative flex h-16 shrink-0 items-center justify-center border-b border-border bg-card">
        <ErpSpan className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-xl font-bold text-primary">{isSidebarOpen ? 'POS' : 'P'}</ErpSpan>
        <ErpButtonRaw type="button" className="absolute end-3 rounded-md p-2 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden" aria-label={t('Close')} onClick={() => dispatch(closeMobile())}><X size={20} /></ErpButtonRaw>
      </ErpDiv>
      <ErpNav ref={scrollRef} data-testid="sidebar-scroll" aria-label={t('ui.navigation')} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-3 overscroll-contain [scrollbar-gutter:stable] scrollbar-thin scrollbar-thumb-muted">
        <ErpUl className="space-y-1 px-3">
          {favoriteItems.length > 0 && <>
            {isSidebarOpen && <ErpLi className="px-3 py-2 text-xs font-semibold text-muted-foreground">{t('Pinned')}</ErpLi>}
            {favoriteItems.map(item => renderItem(item, 'fav-'))}
          </>}
          {menuGroups.map(group => {
            const items = group.items.filter(item => hasPermission(permissions, item.permissions));
            if (!items.length) return null;
            return <React.Fragment key={group.id}>
              {group.id !== 'core' && (isSidebarOpen ? <ErpLi className="px-3 pb-2 pt-5 text-xs font-semibold text-muted-foreground break-words">{t(group.nameKey)}</ErpLi> : <ErpLi aria-hidden="true" className="mx-2 my-3 border-t border-border" />)}
              {items.map(item => renderItem(item))}
            </React.Fragment>;
          })}
          {addonModules.filter(mod => permissions?.includes(mod.permission)).map(mod => <ErpLi key={`addon-${mod.id}`} className="min-w-0">
            <ErpA className={`${baseLink} text-muted-foreground hover:bg-accent hover:text-accent-foreground`} href={mod.url} target="_blank" rel="noopener noreferrer" title={mod.name} aria-label={mod.name} onClick={closeAfterNavigation}>
              <ErpSpan aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center">{itemIcon(mod.icon)}</ErpSpan>
              {isSidebarOpen && <><ErpSpan className={labelClass}>{mod.name}</ErpSpan><ErpSpan className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{t('Addon')}</ErpSpan></>}
            </ErpA>
          </ErpLi>)}
        </ErpUl>
      </ErpNav>
      <ErpDiv className="flex shrink-0 justify-center border-t border-border bg-card p-3">
        <ErpButtonRaw type="button" aria-label={pinned ? t('Unpin') : t('Pin sidebar')} aria-pressed={pinned}
          className={`flex size-10 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${pinned ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
          title={pinned ? t('Unpin') : t('Pin sidebar')} onClick={() => dispatch(togglePinned())}><Pin size={16} className={pinned ? '' : 'rotate-45'} /></ErpButtonRaw>
      </ErpDiv>
    </ErpAside>
  </>
}
export { Sidebar as default }
